import {
  Button,
  Divider,
  Menu, notify,
  RowInput,
  Scrollable,
  useConfirmModal,
} from '@affine/component';
import { useNavigateHelper } from '@affine/core/components/hooks/use-navigate-helper';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import type { Tag } from '@affine/core/modules/tag';
import { TagService } from '@affine/core/modules/tag';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { isNewTabTrigger } from '@affine/core/utils';
import type { Collection } from '@affine/env/filter';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import type { DocMode } from '@blocksuite/affine/blocks';
import {
  ArrowDownSmallIcon,
  SearchIcon,
  ViewLayersIcon,
} from '@blocksuite/icons/rc';
import type { DocRecord } from '@toeverything/infra';
import {
  useLiveData,
  useService,
  useServices,
  WorkspaceService,
  DocsService
} from '@toeverything/infra';
import clsx from 'clsx';
import { useCallback, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Flex, Space, Button as AntdButton } from 'antd'

import { CollectionService } from '../../../modules/collection';
import { usePageHelper } from '../../blocksuite/block-suite-page-list/utils';
import { createTagFilter } from '../filter/utils';
import { SaveAsCollectionButton } from '../view';
import * as styles from './page-list-header.css';
import { PageListNewPageButton } from './page-list-new-page-button';
import { GraphQLService } from "@affine/core/modules/cloud";
import { DiffOutlined } from '@ant-design/icons'
import { createDocumentTemplateMutation, documentTemplatesListQuery } from "@affine/graphql";
import { FAntdTable } from 'izid'
import DraggableModal from "@affine/core/desktop/pages/workspace/task-page/components/DraggableModal";
import dayjs from 'dayjs'
import { useBlockSuiteMetaHelper } from "@affine/core/components/hooks/affine/use-block-suite-meta-helper";

export const PageListHeader = () => {
  const t = useI18n();
  const {
    workspaceService,
    workspaceDialogService,
    workbenchService,
    docsService
  } =
    useServices({
      WorkspaceService,
      WorkspaceDialogService,
      WorkbenchService,
      DocsService
    });

  const workbench = workbenchService.workbench;
  const workspace = workspaceService.workspace;
  const { createEdgeless, createPage } = usePageHelper(workspace.docCollection);

  const title = useMemo(() => {
    return t['com.affine.all-pages.header']();
  }, [t]);

  const handleOpenDocs = useCallback(
    (result: {
      docIds: string[];
      entryId?: string;
      isWorkspaceFile?: boolean;
    }) => {
      const { docIds, entryId, isWorkspaceFile } = result;
      // If the imported file is a workspace file, open the entry page.
      if (isWorkspaceFile && entryId) {
        workbench.openDoc(entryId);
      } else if (!docIds.length) {
        return;
      }
      // Open all the docs when there are multiple docs imported.
      if (docIds.length > 1) {
        workbench.openAll();
      } else {
        // Otherwise, open the only doc.
        workbench.openDoc(docIds[0]);
      }
    },
    [workbench]
  );

  const onImportFile = useCallback(() => {
    track.$.header.importModal.open();
    workspaceDialogService.open('import', undefined, payload => {
      if (!payload) {
        return;
      }
      handleOpenDocs(payload);
    });
  }, [workspaceDialogService, handleOpenDocs]);


  const graphqlService = useService(GraphQLService);

  const [templateVisible, setTemplateVisible] = useState<boolean>(false);
  const openTemplateModal = () => {
    setTemplateVisible(true);
  }

  const templateTableRef = useRef()
  const columns = [
    {
      title: '模板名称',
      dataIndex: 'name',
    },
    {
      title: '创建者',
      dataIndex: 'creator',
      width: 180,
      render (value) {
        return value?.name
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 180,
      render (value) {
        return dayjs(value).format('YYYY-MM-DD HH:mm:ss')
      }
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      width: 180,
      render (value) {
        return dayjs(value).format('YYYY-MM-DD HH:mm:ss')
      }
    },
    {
      title: '更多操作',
      key: 'action',
      width: 120,
      render (_, row) {
        return <Space>
          <AntdButton type={'link'} onClick={() => useAndOpenTemplate(row)}>使用</AntdButton>
        </Space>
      }
    }
  ]
  const GetTemplateList = ({ current, pageSize }) => {
    return new Promise(resolve => {
      graphqlService.gql({
        query: documentTemplatesListQuery,
        variables: {
          input: {
            page: current,
            pageSize,
            filter: {
              workspaceId: workspace.id
            }
          }
        }
      }).then(res => {
        const data = res.documentTemplates
        resolve({
          code: '0',
          data: data.list,
          total: data.total
        })
      }).catch(err => err)
    })
  }
  const createTemplate = () => {
    const page = createPage(undefined, true)
    graphqlService.gql({
      query: createDocumentTemplateMutation,
      variables: {
        input: {
          name: '未命名',
          documentId: page.id,
          workspaceId: workspace.id
        }
      }
    }).then(() => {
      notify.success({
        title: '模板创建成功',
      });
    }).catch(e => e)
  }
  const { duplicate } = useBlockSuiteMetaHelper()
  const useAndOpenTemplate = row => {
    const page = createPage(undefined, true)
    duplicate(row.documentId, true, page.id)
    notify.success({
      title: '已使用模板创建文档',
    });
  }

  return (
    <div className={ styles.docListHeader }>
      <div className={ styles.docListHeaderTitle }>
        <DiffOutlined />
        { title }
      </div>
      <Flex align={ 'center' } gap={ 25 } style={ { marginBottom: '20px' } }>
        <PageListNewPageButton
          size="small"
          testId="new-page-button-trigger"
          onCreateEdgeless={ e =>
            createEdgeless(isNewTabTrigger(e) ? 'new-tab' : true)
          }
          onCreatePage={ e =>
            createPage('page' as DocMode, isNewTabTrigger(e) ? 'new-tab' : true)
          }
          onCreateDoc={ e => {
            // const docList = docsService.list.docs$.value
            // const doc = docList.at(-1)
            // doc.meta$.setValue({ ...doc.meta$.getValue(), demo: '测试demo' })
            // console.log('文档列表', doc.meta$.getValue())
            const page = createPage(undefined, isNewTabTrigger(e) ? 'new-tab' : true)
            // page.meta$.setValue({ ...page.meta$.getValue(), demo: '123' })
            // console.log('这里是返回的page', page)
            return page
          } }
          onImportFile={ onImportFile }
        >
          <div style={ { display: 'flex', justifyContent: 'start', alignItems: 'center' } }>
            <div style={ { marginRight: '15px' } }>
              <svg t="1749176308598" className="icon" viewBox="0 0 1024 1024" version="1.1"
                   xmlns="http://www.w3.org/2000/svg" p-id="23135" width="20" height="20">
                <path
                  d="M943.36 258.13c-8.35-8.34-19.63-13.03-31.43-13.03H778.47V111.38c1.19-10.94-2.09-21.9-9.19-30.32a39.73 39.73 0 0 0-28.21-14.24h-637.2c-10.96 0.65-21.19 5.79-28.21 14.24-7.1 8.42-10.47 19.38-9.11 30.32v806.69c-0.08 21.16 16.24 38.71 37.31 40.11h808.06c11.8 0 23.08-4.73 31.43-13.1 8.41-8.34 13.07-19.69 13.07-31.45v-624c0.01-11.78-4.65-23.14-13.06-31.5zM200.01 557.04h311.44v89.15H200.01v-89.15z m444.9 267.44h-444.9v-89.15h444.9v89.15z m0-445.71h-444.9V200.49h444.9v178.28z m222.52 490.28h-88.95V334.27h88.95v534.78z"
                  fill="#144ced" p-id="23136"></path>
              </svg>
            </div>
            <div style={ { marginRight: '70px' } }>
              <p style={ { textAlign: 'left', fontWeight: 'initial' } }>{ t['New Page']() }</p>
              <p style={ {
                textAlign: 'left',
                color: '#8d9197',
                fontSize: '12px',
                marginTop: '3px',
                fontWeight: 'initial',
              } }>新建文档开始创作</p>
            </div>
          </div>
        </PageListNewPageButton>
        <div className={ styles.pageListNewBtn } onClick={ openTemplateModal }>
          <div style={ { display: 'flex', justifyContent: 'start', alignItems: 'center' } }>
            <div style={ { marginRight: '15px' } }>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M14 13.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" fill="#5B65F5"></path>
                <path
                  d="M10.65 2.85a2 2 0 012.45-1.42l7.7 2.07a2 2 0 011.42 2.45l-2.07 7.7a2 2 0 01-2.45 1.41L10 13a2 2 0 01-1.41-2.45l2.06-7.7z"
                  fill="#FCCA03"></path>
                <path d="M13.89 10.3a1 1 0 011.73 0l5.77 10a1 1 0 01-.87 1.5H9a1 1 0 01-.87-1.5l5.77-10z"
                      fill="#F54A45"></path>
              </svg>
            </div>
            <div style={ { marginRight: '70px' } }>
              <p style={ { textAlign: 'left', fontWeight: 'initial' } }>模板库</p>
              <p style={ {
                textAlign: 'left',
                color: '#8d9197',
                fontSize: '12px',
                marginTop: '3px',
                fontWeight: 'initial',
              } }>选择模板快速新建</p>
            </div>
          </div>
        </div>
        {/* <PageListNewPageButton */}
        {/*   style={ { background: 'red' } } */}
        {/*   size="small" */}
        {/*   testId="new-page-button-trigger" */}
        {/*   // onCreateEdgeless={ e => createEdgeless(isNewTabTrigger(e) ? 'new-tab' : true)} */}
        {/*   // onCreatePage={ e => { */}
        {/*   //   const page = createPage('page' as DocMode, isNewTabTrigger(e) ? 'new-tab' : true) */}
        {/*   //   savePageToTemplateAsync(page.id) */}
        {/*   //   return page */}
        {/*   // }} */}
        {/*   // onCreateDoc={ e => { */}
        {/*   //   const page = createPage(undefined, isNewTabTrigger(e) ? 'new-tab' : true) */}
        {/*   //   savePageToTemplateAsync(page.id) */}
        {/*   //   return page */}
        {/*   // } } */}
        {/*   // onImportFile={ onImportFile } */}
        {/* > */}
        {/*   <div style={ { display: 'flex', justifyContent: 'start', alignItems: 'center' } }> */}
        {/*     <div style={ { marginRight: '15px' } }> */}
        {/*       <svg width="24" height="24" viewBox="0 0 24 24" fill="none"> */}
        {/*         <path d="M14 13.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" fill="#5B65F5"></path> */}
        {/*         <path */}
        {/*           d="M10.65 2.85a2 2 0 012.45-1.42l7.7 2.07a2 2 0 011.42 2.45l-2.07 7.7a2 2 0 01-2.45 1.41L10 13a2 2 0 01-1.41-2.45l2.06-7.7z" */}
        {/*           fill="#FCCA03"></path> */}
        {/*         <path d="M13.89 10.3a1 1 0 011.73 0l5.77 10a1 1 0 01-.87 1.5H9a1 1 0 01-.87-1.5l5.77-10z" */}
        {/*               fill="#F54A45"></path> */}
        {/*       </svg> */}
        {/*     </div> */}
        {/*     <div style={ { marginRight: '70px' } }> */}
        {/*       <p style={ { textAlign: 'left', fontWeight: 'initial' } }>模板库</p> */}
        {/*       <p style={ { */}
        {/*         textAlign: 'left', */}
        {/*         color: '#8d9197', */}
        {/*         fontSize: '12px', */}
        {/*         marginTop: '3px', */}
        {/*         fontWeight: 'initial', */}
        {/*       } }>选择模板快速新建</p> */}
        {/*     </div> */}
        {/*   </div> */}
        {/* </PageListNewPageButton> */}
      </Flex>

      <DraggableModal
        title={ '模板列表' }
        footer={ false }
        open={ templateVisible }
        onCancel={ () => setTemplateVisible(false) }
        width={ 1100 }
        destroyOnHidden
      >
        <Flex justify={'flex-end'} style={{ marginBottom: '10px' }}>
          <AntdButton onClick={createTemplate} type={'primary'}>新建模板</AntdButton>
        </Flex>
        <FAntdTable
          ref={templateTableRef}
          columns={columns}
          api={GetTemplateList}
        />
      </DraggableModal>
    </div>
  );
};
export const CollectionPageListHeader = ({
  collection,
  workspaceId,
}: {
  collection: Collection;
  workspaceId: string;
}) => {
  const t = useI18n();
  const { jumpToCollections } = useNavigateHelper();
  const { collectionService, workspaceService, workspaceDialogService } =
    useServices({
      CollectionService,
      WorkspaceService,
      WorkspaceDialogService,
    });

  const handleJumpToCollections = useCallback(() => {
    jumpToCollections(workspaceId);
  }, [ jumpToCollections, workspaceId ]);

  const handleEdit = useCallback(() => {
    workspaceDialogService.open('collection-editor', {
      collectionId: collection.id,
    });
  }, [ collection, workspaceDialogService ]);

  const workspace = workspaceService.workspace;
  const { createEdgeless, createPage } = usePageHelper(workspace.docCollection);
  const { openConfirmModal } = useConfirmModal();

  const createAndAddDocument = useCallback(
    (createDocumentFn: () => DocRecord) => {
      const newDoc = createDocumentFn();
      collectionService.addPageToCollection(collection.id, newDoc.id);
    },
    [ collection.id, collectionService ]
  );

  const onConfirmAddDocument = useCallback(
    (createDocumentFn: () => DocRecord) => {
      openConfirmModal({
        title: t['com.affine.collection.add-doc.confirm.title'](),
        description: t['com.affine.collection.add-doc.confirm.description'](),
        cancelText: t['Cancel'](),
        confirmText: t['Confirm'](),
        confirmButtonOptions: {
          variant: 'primary',
        },
        onConfirm: () => createAndAddDocument(createDocumentFn),
      });
    },
    [ openConfirmModal, t, createAndAddDocument ]
  );

  const createPageModeDoc = useCallback(
    () => createPage('page' as DocMode),
    [ createPage ]
  );

  const onCreateEdgeless = useCallback(
    () => onConfirmAddDocument(createEdgeless),
    [ createEdgeless, onConfirmAddDocument ]
  );
  const onCreatePage = useCallback(() => {
    onConfirmAddDocument(createPageModeDoc);
  }, [ createPageModeDoc, onConfirmAddDocument ]);
  const onCreateDoc = useCallback(() => {
    onConfirmAddDocument(createPage);
  }, [ createPage, onConfirmAddDocument ]);

  return (
    <div className={ styles.docListHeader }>
      <div className={styles.docListHeaderTitle}>
        <div style={{ cursor: 'pointer' }} onClick={handleJumpToCollections}>
          {t['com.affine.collections.header']()} /
        </div>
        <div className={styles.titleIcon}>
          <ViewLayersIcon />
        </div>
        <div className={styles.titleCollectionName}>{collection.name}</div>
      </div>
      <div className={styles.rightButtonGroup}>
        <Button onClick={handleEdit}>{t['Edit']()}</Button>
        <PageListNewPageButton
          size="small"
          testId="new-page-button-trigger"
          onCreateDoc={onCreateDoc}
          onCreateEdgeless={onCreateEdgeless}
          onCreatePage={onCreatePage}
        >
          <div className={styles.buttonText}>{t['New Page']()}</div>
        </PageListNewPageButton>
      </div>
    </div>
  );
};

export const TagPageListHeader = ({
  tag,
  workspaceId,
}: {
  tag: Tag;
  workspaceId: string;
}) => {
  const tagColor = useLiveData(tag.color$);
  const tagTitle = useLiveData(tag.value$);

  const t = useI18n();
  const { jumpToTags, jumpToCollection } = useNavigateHelper();
  const collectionService = useService(CollectionService);
  const [openMenu, setOpenMenu] = useState(false);

  const handleJumpToTags = useCallback(() => {
    jumpToTags(workspaceId);
  }, [jumpToTags, workspaceId]);

  const saveToCollection = useCallback(
    (collection: Collection) => {
      collectionService.addCollection({
        ...collection,
        filterList: [createTagFilter(tag.id)],
      });
      jumpToCollection(workspaceId, collection.id);
    },
    [collectionService, tag.id, jumpToCollection, workspaceId]
  );

  return (
    <div className={styles.docListHeader}>
      <div className={styles.docListHeaderTitle}>
        <div
          style={{ cursor: 'pointer', lineHeight: '1.4em' }}
          onClick={handleJumpToTags}
        >
          {t['Tags']()} /
        </div>
        <Menu
          rootOptions={{
            open: openMenu,
            onOpenChange: setOpenMenu,
          }}
          contentOptions={{
            side: 'bottom',
            align: 'start',
            sideOffset: 18,
            avoidCollisions: false,
            className: styles.tagsMenu,
          }}
          items={<SwitchTag onClick={setOpenMenu} />}
        >
          <div className={styles.tagSticky}>
            <div
              className={styles.tagIndicator}
              style={{
                backgroundColor: tagColor,
              }}
            />
            <div className={styles.tagLabel}>{tagTitle}</div>
            <ArrowDownSmallIcon className={styles.arrowDownSmallIcon} />
          </div>
        </Menu>
      </div>
      <SaveAsCollectionButton onConfirm={saveToCollection} />
    </div>
  );
};

interface SwitchTagProps {
  onClick: (open: boolean) => void;
}

export const SwitchTag = ({ onClick }: SwitchTagProps) => {
  const t = useI18n();
  const [inputValue, setInputValue] = useState('');
  const tagList = useService(TagService).tagList;
  const filteredTags = useLiveData(
    inputValue ? tagList.filterTagsByName$(inputValue) : tagList.tags$
  );

  const onInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const handleClick = useCallback(() => {
    setInputValue('');
    onClick(false);
  }, [onClick]);

  return (
    <div className={styles.tagsEditorRoot}>
      <div className={styles.tagsEditorSelectedTags}>
        <SearchIcon className={styles.searchIcon} />
        <RowInput
          value={inputValue}
          onChange={onInputChange}
          autoFocus
          className={styles.searchInput}
          placeholder={t['com.affine.search-tags.placeholder']()}
        />
      </div>
      <Divider />
      <div className={styles.tagsEditorTagsSelector}>
        <Scrollable.Root>
          <Scrollable.Viewport
            className={styles.tagSelectorTagsScrollContainer}
          >
            {filteredTags.map(tag => {
              return <TagLink key={tag.id} tag={tag} onClick={handleClick} />;
            })}
            {filteredTags.length === 0 ? (
              <div className={clsx(styles.tagSelectorItem, 'disable')}>
                {t['Find 0 result']()}
              </div>
            ) : null}
          </Scrollable.Viewport>
          <Scrollable.Scrollbar style={{ transform: 'translateX(6px)' }} />
        </Scrollable.Root>
      </div>
    </div>
  );
};

const TagLink = ({ tag, onClick }: { tag: Tag; onClick: () => void }) => {
  const tagColor = useLiveData(tag.color$);
  const tagTitle = useLiveData(tag.value$);
  return (
    <Link
      key={tag.id}
      className={styles.tagSelectorItem}
      data-tag-id={tag.id}
      data-tag-value={tagTitle}
      to={`/tag/${tag.id}`}
      onClick={onClick}
    >
      <div className={styles.tagIcon} style={{ background: tagColor }} />
      <div className={styles.tagSelectorItemText}>{tagTitle}</div>
    </Link>
  );
};
